import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * FINN typography utilities (emitted by libs/design-system/src/atoms/typography/fonts.js
 * via the addUtilities() Tailwind plugin). Each class sets font-size, line-height,
 * font-family, and font-weight, AND applies Capsize-style ::before/::after vertical
 * trim margins. Applying two of these to the same element results in both
 * pseudo-element rules cascading — and the order is fragile.
 *
 * In v3, declaration order in the JS object decided cascade order, and our object
 * happened to put smaller sizes (body-12) AFTER larger sizes (body-16), so body-12
 * won when both were present (e.g. CoreLabel applying body-16-regular as base then
 * body-12-regular when shouldShowLabelAtTop). v4 sorts utility output differently,
 * which reverses the cascade and breaks the visual trim.
 *
 * Tell twMerge that all FINN typography classes are mutually exclusive so cn() keeps
 * only the last one in the input string. This makes the cascade deterministic
 * regardless of v4's emission order.
 */
const finnTypographyClasses = [
  // Web titles
  'web-t1-semibold',
  'web-t2-semibold',
  'web-t3-semibold',
  'web-t3-light',
  'web-t4-semibold',
  'web-t4-light',
  // Mobile titles
  'mobile-t1-semibold',
  'mobile-t2-semibold',
  'mobile-t3-semibold',
  'mobile-t3-light',
  'mobile-t4-semibold',
  'mobile-t4-light',
  // Global titles
  'global-t5-semibold',
  'global-t5-light',
  'global-t6-semibold',
  // Body
  'body-16-light',
  'body-16-regular',
  'body-16-medium',
  'body-16-semibold',
  'body-14-light',
  'body-14-regular',
  'body-14-medium',
  'body-14-semibold',
  'body-12-light',
  'body-12-regular',
  'body-12-medium',
  'body-12-semibold',
  // Links
  'link-16',
  'link-14',
  'link-12',
  // Buttons
  'button-16',
  'button-14',
  'button-12',
];

const customTwMerge = extendTailwindMerge<'finn-typography'>({
  extend: {
    classGroups: {
      'finn-typography': finnTypographyClasses,
    },
  },
});

export const cn = (...inputs: ClassValue[]) => customTwMerge(clsx(inputs));
